import React, { useContext, useState } from 'react';
import { userContext } from "../../stores/users/userContext";
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useForm } from "react-hook-form";
import Avatar from "@mui/material/Avatar";
import DownloadIcon from '@mui/icons-material/Download';
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import getCookies from "../CSRF/getCookies";
import { downloadUrl } from "../constants";

const DnVideos = () => {
    const userStore = useContext(userContext)
    const [csrfToken, setCsrfToken] = useState(getCookies('csrftoken'))
    const { register, handleSubmit, formState: { errors } } = useForm()
    const [download, setDownload] = useState(true)
    const [error, setError] = useState(null)
    const [message, setMessage] = useState(null)
    const [upError, setUpError] = useState(null)
    const [url, setUrl] = useState('')

    const onSubmit = async (data) => {
        setDownload(true)
        // convert data to x-www-form-urlencoded
        const urlEncodedData = new URLSearchParams();
        for (const pair of Object.entries(data)) {
            urlEncodedData.append(pair[0], pair[1])
        }
        const response = await fetch(downloadUrl, {
            method: 'POST',
            mode: 'cors',
            body: urlEncodedData,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-CSRFToken': csrfToken,
            },
        }).then(response => {
            if (response.status === 200) {
                setMessage(`An email will be sent to ${userStore.getUserEmail()} when the video is ready for use.`)
                return response.json()
            } else {
                throw new Error(response.statusText)
            }
        }).catch(error => {
            setError(error.message)
            setDownload(false)
        })
    }

    const onErrorDismiss = () => {
        setUrl('')
        setError(null)
        setDownload(true)
    }

    const onMessageDismiss = () => {
        setMessage(null)
        setUrl('')
        setError(null)
        setDownload(true)
    }

    const onChangeDownload = (event) => {
        setUrl(event.target.value)
        setDownload(false)
    }

    return (
        <Container maxWidth="sm">
            <Box sx={{
                mt: { xs: '70px', sm: '75px', md: '61px' },
                mb: { xs: 4, sm: 6, md: 8 },
                px: { xs: 2, sm: 3, md: 0 },
                width: '100%'
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: { xs: 3, sm: 4 }
                }}>
                    <Avatar sx={{
                        m: 1,
                        width: { xs: 40, sm: 48, md: 56 },
                        height: { xs: 40, sm: 48, md: 56 },
                        bgcolor: 'primary.main'
                    }}>
                        <DownloadIcon sx={{ fontSize: { xs: 16, sm: 20, md: 24 } }} />
                    </Avatar>
                    <Typography
                        component="h1"
                        variant="h5"
                        sx={{
                            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                            textAlign: 'center',
                            fontWeight: 'medium',
                            mt: 1
                        }}
                    >
                        Enter a URL to download a video
                    </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
                    <input hidden {...register('csrfmiddlewaretoken')} value={csrfToken} />
                    <input hidden {...register('token')} value={userStore.getToken()} />

                    <TextField
                        {...register('url')}
                        fullWidth
                        id="url"
                        label="YouTube URL or Video URL"
                        name="url"
                        type="url"
                        value={url}
                        onChange={onChangeDownload}
                        placeholder="https://www.youtube.com/watch?v=..."
                        variant="outlined"
                        sx={{
                            mb: 3,
                            '& .MuiInputBase-root': {
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                            }
                        }}
                    />

                    <Button
                        type="submit"
                        disabled={download}
                        fullWidth
                        variant="contained"
                        size="large"
                        sx={{
                            mt: 1,
                            mb: 2,
                            py: { xs: 1.5, sm: 2 },
                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                            fontWeight: 'bold',
                            borderRadius: 2,
                            textTransform: 'none'
                        }}
                    >
                        Download video
                    </Button>

                    {error && (
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography
                                color="error"
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    bgcolor: 'error.light',
                                    color: 'error.contrastText',
                                    borderRadius: 1,
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    wordBreak: 'break-word'
                                }}
                            >
                                {error}
                            </Typography>
                            <Button
                                fullWidth
                                color="error"
                                variant="contained"
                                size="medium"
                                onClick={onErrorDismiss}
                                sx={{
                                    py: { xs: 1, sm: 1.5 },
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    textTransform: 'none'
                                }}
                            >
                                Dismiss
                            </Button>
                        </Box>
                    )}

                    {message && (
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography
                                color="success.main"
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    bgcolor: 'success.light',
                                    color: 'success.contrastText',
                                    borderRadius: 1,
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    wordBreak: 'break-word'
                                }}
                            >
                                {message}
                            </Typography>
                            <Button
                                fullWidth
                                color="success"
                                variant="contained"
                                size="medium"
                                onClick={onMessageDismiss}
                                sx={{
                                    py: { xs: 1, sm: 1.5 },
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    textTransform: 'none'
                                }}
                            >
                                Dismiss
                            </Button>
                        </Box>
                    )}
                </Box>

                {upError && (
                    <Button
                        fullWidth
                        color="error"
                        variant="contained"
                        size="medium"
                        onClick={onErrorDismiss}
                        sx={{
                            mt: 2,
                            mb: 2,
                            py: { xs: 1, sm: 1.5 },
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            textTransform: 'none'
                        }}
                    >
                        {upError}! Click to dismiss
                    </Button>
                )}
            </Box>
        </Container>
    )
}

export default DnVideos